// 내 일정 데이터 훅 — 각 모듈 API 응답(api/schedule.ts)을 받아 화면용 항목(lib/schedule.ts)으로 합친다.
//
// 세리사무소 포털 홈의 「내 일정」 위젯이 쓴다. 본인 사번(useAuth().user.username)으로만 조회하고 읽기 전용이다.
// 출처마다 따로 부르므로 한 모듈이 안 떠 있거나 실패해도 나머지는 그대로 온다(failed·missing 으로 알린다).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../lib/locale';
import type { MessageKey } from '../../lib/locale.messages';
import { fetchScheduleSources } from '../../api/schedule';
import type { SourceResult } from '../../api/schedule';
import { attendItems, leaveItems, pjtTaskItems, pjtTodoItems, sortItems, tripItems } from '../../lib/schedule';
import type { ScheduleItem, ScheduleLabels, ScheduleSource } from '../../lib/schedule';

/** 출처가 속한 모듈 — 실패 안내에 모듈 이름을 쓴다 */
export const SOURCE_MODULE: Record<ScheduleSource, string> = {
  'pjt-task': 'pjt', 'pjt-todo': 'pjt', 'hr-leave': 'hr', 'ops-trip': 'ops', 'hr-attend': 'hr',
};

export const SOURCES: ScheduleSource[] = ['pjt-task', 'pjt-todo', 'hr-leave', 'ops-trip', 'hr-attend'];

/** 현재 언어의 화면 문구 — 순수 함수(lib/schedule.ts)에 넘긴다 */
export function useScheduleLabels(): ScheduleLabels {
  const { lang, t } = useT();
  return useMemo(() => {
    const mk = (prefix: string, codes: number[]) => {
      const o: Record<number, string> = {};
      codes.forEach(c => { o[c] = t(`${prefix}.${c}` as MessageKey); });
      return o;
    };
    return {
      leaveStatus: mk('schedule.leave', [2, 3, 5, 6]),
      tripStatus: mk('schedule.trip', [1, 2, 3, 4]),
      taskStatus: mk('schedule.task', [1, 2, 3, 4]),
      todoLate: t('schedule.status.late'),
      attendAbsent: t('schedule.status.attendAbsent'),
      attendFixPending: t('schedule.status.attendFix'),
      attendReview: t('schedule.status.attendReview'),
      taskSub: t('schedule.status.sub'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
}

export interface ScheduleState {
  items: ScheduleItem[];
  loading: boolean;
  /** 로그인 정보에서 사번을 못 읽었다 */
  noUser: boolean;
  /** 응답은 왔으나 실패한 출처 */
  failed: ScheduleSource[];
  /** 모듈이 연결되어 있지 않은 출처(404·502·503) */
  missing: ScheduleSource[];
  /** 한 번이라도 응답을 받았는가 — 첫 조회 전의 「일정 없음」 깜빡임을 막는다 */
  loaded: boolean;
  reload: () => void;
}

/** [from, to](둘 다 포함) 기간의 본인 일정. 기간·재조회가 바뀌면 다시 받고, 늦게 도착한 옛 응답은 버린다 */
export function useSchedule(from: string, to: string): ScheduleState {
  const { user } = useAuth();
  const empCd = user ? user.username : '';
  const labels = useScheduleLabels();
  const [results, setResults] = useState<SourceResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rev, setRev] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!empCd) return undefined;
    const mine = ++seq.current;
    const ctl = new AbortController();
    setLoading(true);
    fetchScheduleSources(empCd, from, to, ctl.signal)
      .then(r => { if (mine === seq.current) { setResults(r); setLoaded(true); } })
      .catch(() => { /* 취소(AbortError) — 새 조회가 이어받는다 */ })
      .then(() => { if (mine === seq.current) setLoading(false); });
    return () => ctl.abort();
  }, [empCd, from, to, rev]);

  const items = useMemo(() => {
    const all: ScheduleItem[] = [];
    results.forEach(r => {
      if (!r.ok) return;
      if (r.source === 'pjt-task') all.push(...pjtTaskItems(r.body, labels));
      else if (r.source === 'pjt-todo') all.push(...pjtTodoItems(r.body, labels));
      else if (r.source === 'hr-leave') all.push(...leaveItems(r.body, labels));
      else if (r.source === 'ops-trip') all.push(...tripItems(r.body, from, to, labels));
      else if (r.source === 'hr-attend') all.push(...attendItems(r.body, labels));
    });
    // 내 업무는 기간 조회가 없어 마감일이 기간 안인 것만 남긴다
    return sortItems(all.filter(i => i.source !== 'pjt-todo' || (i.start >= from && i.start <= to)));
  }, [results, labels, from, to]);

  const failed: ScheduleSource[] = [];
  const missing: ScheduleSource[] = [];
  results.forEach(r => {
    if (r.ok) return;
    (r.reason === 'not-installed' ? missing : failed).push(r.source);
  });

  return { items, loading, noUser: !empCd, failed, missing, loaded, reload: () => setRev(n => n + 1) };
}
